// 揭晓排序器（EP-007-S2 / 主架构 §5.3 / §6.4 步骤 D；ADR-004 §2.5）。
//
// 职责：在 `trip` 尚存时从存档构造「演出快照」revealSnapshot，供 RevealSequence 只读演出。
// 快照构造必须在 pureSettle（关闸门）**之前**，以保证任何中断都不丢礼物——
// 礼物已在 inventory 落盘后才播演出（主架构 §6.4）。
//
// 本文件在 src/runtime（非 domain），允许 Date.now / Math.random；但本实现不依赖随机源，
// 留言选取由存档状态（目的地 / 是否长旅行 / tripCount）确定性推导，刷新可复现。
import type { SaveDataV1, DestinationId, Rarity } from '../save/types';
import { GIFT_BY_ID } from '../domain/gifts/giftDef';

export interface PerGiftInfo {
  giftId: string;
  name: string;
  rarity: Rarity;
  isNew: boolean; // 本次旅行前尚未拥有（用于演出「新获得」标记）
  index: number; // 0-based 序列，用于飘落顺序
}

export interface RevealSnapshot {
  destinationId: DestinationId;
  giftIds: string[];
  noteId: string;
  perGift: PerGiftInfo[];
  /** 🆕 P6-POLISH-ENG-002：本次是否空手归来（giftIds 为 0 但有空手留言保底）。 */
  emptyHanded: boolean;
}

// 稀有度档位名（非颜色冗余通道，R9 / GDD 03 §②-2.3 / ⑧-C1~C4）。
export const RARITY_NAME: Record<Rarity, string> = {
  N: '普通',
  U: '少见',
  R: '稀有',
  L: '传说',
};

// 档位 → 点数（1/2/3/4，角标用，颜色固定不随档位变化）。
export const RARITY_TIER: Record<Rarity, number> = {
  N: 1,
  U: 2,
  R: 3,
  L: 4,
};

// 点数 → 档位名（RarityDots 由 tier 反查名称，避免颜色之外的冗余通道丢失文字）。
export const RARITY_NAME_BY_TIER: Record<number, string> = {
  1: '普通',
  2: '少见',
  3: '稀有',
  4: '传说',
};

// 小狗留言（来源 design/ux/04-dog-notes.md，10 条；v2 起明信片不再作礼物，改为 mailbox 信件——
// 见 src/runtime/postcardNotes.ts 共 12 条 ≤25 字配文，由 src/domain/travel/postcardRoll.ts 触发）。
// 10 条 ≤20 字、无感叹号、第一人称、不剧透礼物名。
export const DOG_NOTES: Record<string, string> = {
  'riverside-wet-paws': '水凉凉的，没敢走太深。',
  'riverside-reed-bend': '风把芦苇吹得一弯腰，我就跟着弯了一下。',
  'forest-leaf-first': '脚踩下去，叶子比我先听见声音。',
  'forest-squirrel-stared': '有只松鼠看了我很久，最后它先跑了。',
  'hill-rooftop-direction': '站得高一点，能看见我们家屋顶的方向。',
  'hill-ears-flutter': '风把我耳朵吹得直抖，我没躲。',
  'town-many-smells': '街上的味道很多，我只记得住一种。',
  'town-hand-on-head': '有只手摸了我的头，我没有摇尾巴。',
  'longtrip-tired-legs': '出去走了很远，腿有点酸。',
  'longtrip-warm-door-light': '回到家了。门口那盏灯还是暖的。',
};

// 🆕 P6-POLISH-ENG-002（文案 06 §2）：空手归来的专用留言池，16 条，逐字照抄设计文档。
//
// 为什么独立成池：带礼物时的 10 条都在"讲路上发生了什么 + 含蓄暗示礼物"；空手时若复用它们，
// 玩家会盯着画面等礼物飘下来却等不到，落差变成困惑。空手必须要有一句
// 「我玩得挺好，只是没带东西」把落差接住。
//
// 视角纪律：第一人称（小狗说话），**与 §C 在家状态文案的第三人称旁白刻意区分**，两池勿混用。
// 写作纪律继承 04-dog-notes.md §1 —— ≤20 字、无感叹号、无阿拉伯数字、**不剧透礼物名**、
// 不点目的地全名；唯一放宽的是"空手文案可以直白说自己没带东西"（空手本来就没有礼物可剧透）。
export const EMPTY_HANDS_NOTES: Record<string, string> = {
  // A 组 · 什么都没带回来（松弛 / 开心，8 条）
  'emptyhands-ran-happy': '今天什么都没带，跑得很开心。',
  'emptyhands-forgot-to-carry': '一路都在看，忘了要叼点什么。',
  'emptyhands-remembered-late': '跑了一路，回来才想起来手是空的。',
  'emptyhands-sun-on-back': '太阳晒得背发热，我躺了一会儿。',
  'emptyhands-napped': '找了个暖的地方，睡着了一会儿。',
  'emptyhands-smelled-wind': '风很合适，我一路都在闻。',
  'emptyhands-light-legs': '回来的时候，腿是轻的。',
  'emptyhands-quiet-road': '今天没什么事，路上很安静。',
  // B 组 · 碰到了点什么，但没带回来（8 条）
  'emptyhands-met-a-dog': '碰到一只狗，我们互相闻了闻。',
  'emptyhands-rain-passed': '下了一小会儿雨，我躲在檐下。',
  'emptyhands-dropped-it': '叼了一根，走到一半掉了。',
  'emptyhands-someone-called': '有人叫我的名字，我没过去。',
  'emptyhands-sat-long': '在一个地方坐了很久，忘了时间。',
  'emptyhands-butterfly': '追了一只白蝴蝶，追到一半忘了。',
  'emptyhands-dusk-home': '天要黑的时候，我记起该回家了。',
  'emptyhands-too-big': '看到的东西都太大，叼不动。',
};

// 空手池按顺序确定性轮换（tripCount % 16），**目的地无关** ——
// 空手是一次"没发生什么"的旅行，地点信息应当弱化；且 16 条再按 4 个目的地切成 4 条/组，
// 同一条的出现频率会高到玩家察觉。
const EMPTY_HANDS_POOL: readonly string[] = [
  'emptyhands-ran-happy',
  'emptyhands-forgot-to-carry',
  'emptyhands-remembered-late',
  'emptyhands-sun-on-back',
  'emptyhands-napped',
  'emptyhands-smelled-wind',
  'emptyhands-light-legs',
  'emptyhands-quiet-road',
  'emptyhands-met-a-dog',
  'emptyhands-rain-passed',
  'emptyhands-dropped-it',
  'emptyhands-someone-called',
  'emptyhands-sat-long',
  'emptyhands-butterfly',
  'emptyhands-dusk-home',
  'emptyhands-too-big',
];

const DEST_NOTES: Record<DestinationId, [string, string]> = {
  riverside: ['riverside-wet-paws', 'riverside-reed-bend'],
  forest: ['forest-leaf-first', 'forest-squirrel-stared'],
  hill: ['hill-rooftop-direction', 'hill-ears-flutter'],
  town: ['town-many-smells', 'town-hand-on-head'],
};
const LONGTRIP_NOTES: [string, string] = ['longtrip-tired-legs', 'longtrip-warm-door-light'];

// 去重键（池 → 键一一对应）：'longtrip'（虚拟键）/ dest。
// 导出供 store 在结算后回写 `lastNoteIdByDestination`，保证「选」与「记」用同一套规则
// （GDD 02 §②-2.7 的连续不重复；此前该字段只有读没有写，去重实际未生效）。
// `lastNoteIdByDestination` 是 Record<string, string>，虚拟键无需改存档类型 / sanitize。
// v2：移除 `'postcard'` 虚拟键（明信片不再是礼物）。
// 🆕 空手池的去重虚拟键（07 §B.5）：与 'longtrip' / dest 同级，写进同一个 lastNoteIdByDestination
//   Record —— 虚拟键本质是该池的名字的字符串常量，不需要改存档类型 / sanitize。
export const EMPTY_HANDS_DEDUPE_KEY = 'emptyhands';

export function noteDedupeKey(isLongTrip: boolean, dest: DestinationId): string {
  return isLongTrip ? 'longtrip' : dest;
}

// 选留言。
// 🆕 P6-POLISH-ENG-002（07 §B.5）**池优先级改为：空手池 > 长旅行池 > 目的地池**。
//   理由：空手是一次特殊事件，叙事优先级最高；若一次旅行既是长旅行又空手，
//   说"没带东西"比说"腿有点酸"更贴切（且避免玩家期待礼物）。
// 连续不重复（GDD 02 §②-2.7）：若与上一次同键留言相同，则换另一条。
function pickNoteId(
  save: SaveDataV1,
  dest: DestinationId,
  isLongTrip: boolean,
  emptyHanded = false,
): string {
  if (emptyHanded) {
    return pickEmptyHandsNoteId(save);
  }
  const pool = isLongTrip ? LONGTRIP_NOTES : DEST_NOTES[dest];
  const dedupeKey = noteDedupeKey(isLongTrip, dest);
  const last = save.lastNoteIdByDestination[dedupeKey];
  let idx = save.stats.tripCount % pool.length;
  if (pool[idx] === last && pool.length > 1) idx = (idx + 1) % pool.length;
  return pool[idx] ?? pool[0];
}

// 空手留言：idx = tripCount % 16，连续不重复（与 DOG_NOTES 同算法）。
function pickEmptyHandsNoteId(save: SaveDataV1): string {
  const pool = EMPTY_HANDS_POOL;
  const last = save.lastNoteIdByDestination[EMPTY_HANDS_DEDUPE_KEY];
  let idx = save.stats.tripCount % pool.length;
  if (pool[idx] === last && pool.length > 1) idx = (idx + 1) % pool.length;
  return pool[idx] ?? (pool[0] as string);
}

// 构造演出快照（trip 仍存）。trip 为 null 时返回安全空快照（调用方应保证 awaitingReveal）。
export function revealSnapshot(save: SaveDataV1): RevealSnapshot {
  const trip = save.trip;
  if (!trip) {
    return {
      destinationId: 'riverside',
      giftIds: [],
      noteId: '',
      perGift: [],
      emptyHanded: false,
    };
  }

  const giftIds = trip.dropResult ? trip.dropResult.giftIds : [];
  const owned = new Set(save.inventory.map((s) => s.giftId));
  const perGift: PerGiftInfo[] = giftIds.map((giftId, index) => {
    const def = GIFT_BY_ID[giftId];
    return {
      giftId,
      name: def?.name ?? giftId,
      rarity: def?.rarity ?? 'N',
      isNew: !owned.has(giftId),
      index,
    };
  });

  // 🆕 空手判定：$roll.ts 的红线保证 giftIds 恒 ≥ 1，所以 giftIds 为空**只可能**来自 planReturn 的空手分支。
  //   这条反推是本 fibre 的关键假设 —— 若将来 roll.ts 允许返回空数组，这里必须改成显式传参。
  const emptyHanded = trip.dropResult !== null && giftIds.length === 0;
  const noteId = pickNoteId(save, trip.destinationId, trip.isLongTrip, emptyHanded);
  return { destinationId: trip.destinationId, giftIds, noteId, perGift, emptyHanded };
}
